/**
 * ============================================================================
 * 🟣 CHECKLIST QUALITÉ POUR TOUT NOUVEAU PANNEAU / NOTIFICATION (TOAST) :
 * ============================================================================
 * - [ ] Le message est-il visible immédiatement (z-index élevé, au premier plan) ?
 * - [ ] La couleur correspond-elle au type (success/error/info/warning/copy) ?
 * - [ ] Y a-t-il une icône claire associée ?
 * - [ ] Le panneau est-il fermable (croix [✕] ou auto-dismiss) ?
 * - [ ] Y a-t-il un délai de fermeture automatique (4 secondes par défaut) ?
 * - [ ] Est-ce que le contraste est suffisant (WCAG AA) ?
 * - [ ] Est-ce que le rôle ARIA est présent (role="alert" / aria-live="polite") ?
 * - [ ] Est-ce que le panneau est responsive (mobile-first, ne dépasse pas) ?
 * - [ ] Est-ce que le texte est clair et concis ?
 * - [ ] Est-ce que le comportement est testé ?
 * ============================================================================
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, Copy, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'copy';

export interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  duration?: number;
}

interface ToastContextType {
  toast: {
    success: (message: string, title?: string, duration?: number) => void;
    error: (message: string, title?: string, duration?: number) => void;
    info: (message: string, title?: string, duration?: number) => void;
    warning: (message: string, title?: string, duration?: number) => void;
    copy: (message: string, title?: string, duration?: number) => void;
    show: (options: Omit<ToastItem, 'id'>) => void;
    remove: (id: string) => void;
  };
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const TOAST_STYLES: Record<
  ToastType,
  {
    bg: string;
    border: string;
    text: string;
    badgeBg: string;
    icon: React.ComponentType<{ className?: string }>;
    defaultTitle: string;
  }
> = {
  success: {
    bg: 'bg-emerald-950/90 dark:bg-emerald-950/90 text-emerald-50',
    border: 'border-emerald-500/40 shadow-emerald-950/50 shadow-lg',
    text: 'text-emerald-200',
    badgeBg: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    icon: CheckCircle2,
    defaultTitle: 'Succès',
  },
  copy: {
    bg: 'bg-zinc-950/95 dark:bg-zinc-950/95 text-zinc-50',
    border: 'border-primary/50 shadow-primary/20 shadow-xl',
    text: 'text-zinc-200',
    badgeBg: 'bg-primary/20 text-primary-ink border border-primary/30',
    icon: Copy,
    defaultTitle: 'Presse-papier',
  },
  error: {
    bg: 'bg-rose-950/90 dark:bg-rose-950/90 text-rose-50',
    border: 'border-rose-500/40 shadow-rose-950/50 shadow-lg',
    text: 'text-rose-200',
    badgeBg: 'bg-rose-500/20 text-rose-400 border border-rose-500/30',
    icon: AlertCircle,
    defaultTitle: 'Erreur',
  },
  warning: {
    bg: 'bg-amber-950/90 dark:bg-amber-950/90 text-amber-50',
    border: 'border-amber-500/40 shadow-amber-950/50 shadow-lg',
    text: 'text-amber-200',
    badgeBg: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
    icon: AlertTriangle,
    defaultTitle: 'Attention',
  },
  info: {
    bg: 'bg-sky-950/90 dark:bg-sky-950/90 text-sky-50',
    border: 'border-sky-500/40 shadow-sky-950/50 shadow-lg',
    text: 'text-sky-200',
    badgeBg: 'bg-sky-500/20 text-sky-400 border border-sky-500/30',
    icon: Info,
    defaultTitle: 'Information',
  },
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    ({ type, message, title, duration = 4000 }: Omit<ToastItem, 'id'>) => {
      const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      const newToast: ToastItem = { id, type, message, title, duration };

      setToasts((prev) => [...prev.slice(-3), newToast]); // Keep max 4 toasts simultaneously

      if (duration > 0) {
        setTimeout(() => {
          remove(id);
        }, duration);
      }
    },
    [remove]
  );

  const success = useCallback(
    (message: string, title?: string, duration?: number) =>
      show({ type: 'success', message, title, duration }),
    [show]
  );

  const error = useCallback(
    (message: string, title?: string, duration?: number) =>
      show({ type: 'error', message, title, duration }),
    [show]
  );

  const info = useCallback(
    (message: string, title?: string, duration?: number) =>
      show({ type: 'info', message, title, duration }),
    [show]
  );

  const warning = useCallback(
    (message: string, title?: string, duration?: number) =>
      show({ type: 'warning', message, title, duration }),
    [show]
  );

  const copy = useCallback(
    (message: string, title?: string, duration?: number) =>
      show({ type: 'copy', message, title: title || 'Copié !', duration: duration || 3500 }),
    [show]
  );

  return (
    <ToastContext.Provider
      value={{
        toast: { success, error, info, warning, copy, show, remove },
      }}
    >
      {children}

      {/* Global Toast Container */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[999999] flex flex-col gap-2.5 max-w-[calc(100vw-2rem)] sm:max-w-md pointer-events-none"
      >
        {toasts.map((t) => {
          const style = TOAST_STYLES[t.type];
          const IconComponent = style.icon;

          return (
            <div
              key={t.id}
              role="alert"
              className={`pointer-events-auto flex items-start gap-3 p-3.5 sm:p-4 rounded-xl border backdrop-blur-xl animate-in fade-in slide-in-from-top-3 duration-200 ${style.bg} ${style.border}`}
            >
              {/* Icon */}
              <div className={`p-1.5 rounded-lg shrink-0 mt-0.5 ${style.badgeBg}`}>
                <IconComponent className="w-4 h-4" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pr-1">
                {t.title && (
                  <div className="font-semibold text-xs sm:text-sm tracking-tight text-white mb-0.5">
                    {t.title}
                  </div>
                )}
                <p className={`text-xs sm:text-sm leading-relaxed ${style.text}`}>
                  {t.message}
                </p>
              </div>

              {/* Close Button */}
              <button
                type="button"
                onClick={() => remove(t.id)}
                aria-label="Fermer la notification"
                className="shrink-0 p-1 -mr-1 text-zinc-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
